import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Table } from 'semantic-ui-react';

import { CustomSegment } from '../../../../components';
import { HtmlService } from '../../../../services';
import { putActivity } from '../../../../store/actions';
import {
  buildFinancialSummary,
  financialSummaryHeadings,
  financialSummaryRows,
} from '../../../../utils/activity/financialSummary';
import {
  CKField,
  IncludeInReportToggle,
  saveReportInclusion,
} from '../reportFields';

import './FinancialSummary.scss';

const ROW_CLASS = {
  program: 'financial-summary__program',
  result: 'financial-summary__result',
  note: 'financial-summary__note',
};

const parseInclusions = (raw) => {
  try {
    return JSON.parse(raw || '{}');
  } catch {
    return {};
  }
};

/**
 * TAK-2086: the activity's financials as a short table on the Documentation
 * page — the same figures as the Activity Plan (shared calculation), with
 * expenses shown as negatives so each column adds down to the Financial
 * Result. Projected and, once green lit, Actual. In-kind support is noted
 * under the Result but not counted. The same table appears in the report as
 * the Financial Result appendix.
 */
const FinancialSummary = ({ currentActivity }) => {
  const { t } = useTranslation();
  const summary = buildFinancialSummary(currentActivity);

  return (
    <Table basic="very" unstackable className="financial-summary">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell />
          {financialSummaryHeadings({ summary, t }).map((heading) => (
            <Table.HeaderCell key={heading} textAlign="right">
              {heading}
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {financialSummaryRows({ summary, t }).map((row) => (
          <Table.Row key={row.key} className={ROW_CLASS[row.kind]}>
            <Table.Cell>{row.label}</Table.Cell>
            {row.cells.map((cell, i) => (
              <Table.Cell
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                textAlign="right"
                className={
                  cell.negative
                    ? 'financial-summary__amount financial-summary__amount--negative'
                    : 'financial-summary__amount'
                }
              >
                {cell.text}
              </Table.Cell>
            ))}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

/**
 * The Documentation page card around the table, with its "Show in report"
 * toggle (on by default) for the report's Financial Result appendix, and a
 * "Financial note" below it — shown under the table in the appendix, with
 * its own "Show in report" toggle (also on by default).
 */
export const FinancialSummaryCard = ({ currentActivity, pageDisabled }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [inclusions, setInclusions] = useState(() =>
    parseInclusions(currentActivity.reportInclusions)
  );

  useEffect(() => {
    setInclusions(parseInclusions(currentActivity.reportInclusions));
  }, [currentActivity.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async (key, value) => {
    setInclusions((prev) => ({ ...prev, [key]: value }));
    const merged = await saveReportInclusion({
      activityId: currentActivity.id,
      key,
      value,
    });
    if (merged) {
      setInclusions(merged);
    }
  };

  // Saves the note and tells any open Report Builder tab, as the other
  // Documentation text fields do.
  const handlePutActivity = (type, data) => {
    dispatch(putActivity(type, data));
    if (data.financialNote !== undefined) {
      const ch = new BroadcastChannel('takso_activity');
      ch.postMessage({
        type: 'financialNote',
        activityId: currentActivity.id,
        financialNote: data.financialNote,
      });
      ch.close();
    }
  };

  // archived (Completed) activities are read-only, as on the other
  // Documentation text fields
  const isArchived = Number(currentActivity.statusId) === 3;
  const fieldDisabled = isArchived || pageDisabled;

  return (
    <CustomSegment
      className="financial-summary-card"
      title={t('activity.documentation.financialSummary.title')}
      rightHeader={
        <IncludeInReportToggle
          included={inclusions.financialSummary !== false}
          onToggle={(value) => handleToggle('financialSummary', value)}
          disabled={pageDisabled}
        />
      }
      children={
        <>
          <FinancialSummary currentActivity={currentActivity} />
          <div className="financial-summary__note-field">
            <CKField
              key={`financial-note-${currentActivity.id}`}
              label={t('activity.documentation.financialSummary.note_label')}
              labelToggle={
                <IncludeInReportToggle
                  included={inclusions.financialNote !== false}
                  onToggle={(value) => handleToggle('financialNote', value)}
                  disabled={fieldDisabled}
                />
              }
              placeholder={t(
                'activity.documentation.financialSummary.note_placeholder'
              )}
              initialData={HtmlService.convertToInnerHtml({
                children: currentActivity?.financialNote || '',
                richText: true,
                htmlConversionStyle: 'para',
              })}
              fieldName="financialNote"
              putActivity={handlePutActivity}
              disabled={fieldDisabled}
            />
          </div>
        </>
      }
      extraMargin
      disabled={pageDisabled}
    />
  );
};

export default FinancialSummary;
